import { useEffect, useCallback, lazy, Suspense } from 'react';

import { useAppStore, type IAppStore } from '@/store';
import {
  useProjectInit,
  useNotificationHandler,
  useCategoryDeeplink,
  usePresetDeeplink,
  useAutoGeneration,
  useCreditsSubscription,
  useLocalNotification,
} from '@/hooks';
import Header from '@/components/Header';
import Toast from '@/components/shared/Toast';
import { ResultOverlay } from './ResultOverlay';
import { useAppContentStyles } from './styles';
import { isDesktop } from '@/consts/device';
import { removeTaskIdFromUrl } from '@/utils/ui';
import AppLoader from '../shared/AppLoader';

const DesktopLayout = lazy(() =>
  import('./DesktopLayout').then((m) => ({ default: m.DesktopLayout }))
);
const MobileLayout = lazy(() =>
  import('./MobileLayout').then((m) => ({ default: m.MobileLayout }))
);

const IntroVideo = lazy(() => import('@/components/IntroVideo'));

// External selector for stable reference
const selector = (store: IAppStore) => ({
  screen: store.screen,
  setScreen: store.setScreen,
  resetCreate: store.resetCreate,
  isRemoteProject: store.isRemoteProject,
  rightPanelTab: store.rightPanelTab,
  setRightPanelTab: store.setRightPanelTab,
  viewCrop: store.viewCrop,
  setViewCrop: store.setViewCrop,
  viewSize: store.viewSize,
  setViewSize: store.setViewSize,
  globalToast: store.toast,
});

interface AppContentProps {
  showIntroVideo: boolean;
  onIntroClose: () => void;
}

export const AppContent = ({
  showIntroVideo,
  onIntroClose,
}: AppContentProps) => {
  const classes = useAppContentStyles();
  const {
    screen,
    setScreen,
    resetCreate,
    isRemoteProject,
    rightPanelTab,
    setRightPanelTab,
    viewCrop,
    setViewCrop,
    viewSize,
    setViewSize,
    globalToast,
  } = useAppStore(selector);

  useCategoryDeeplink();
  usePresetDeeplink();
  const { isLoading: isProjectLoading } = useProjectInit();
  useCreditsSubscription();
  useAutoGeneration();
  const { notificationHandler, hasTaskId } = useNotificationHandler();
  const { toast: notification, hideToast: hideNotification } =
    useLocalNotification();

  useEffect(() => {
    if (hasTaskId) notificationHandler();
  }, [hasTaskId, notificationHandler]);

  useEffect(() => {
    if (isRemoteProject) onIntroClose();
  }, [isRemoteProject, onIntroClose]);

  const handleCloseResult = useCallback(() => {
    resetCreate();
    setScreen('files');
    removeTaskIdFromUrl();
  }, [resetCreate, setScreen]);

  if (isProjectLoading) {
    return <AppLoader />;
  }

  const showOverlay = screen === 'result' || screen === 'loading';

  return (
    <>
      {showIntroVideo && (
        <Suspense fallback={null}>
          <IntroVideo
            videoUrl="https://cdn-cms-uploads.picsart.com/cms-uploads/6db56a8a-f255-4d33-b297-9fbccba62b10.mp4"
            mobileVideoUrl="https://cdn-cms-uploads.picsart.com/cms-uploads/dfde9a45-da59-4eff-9cea-fa2d5144dcbd.MP4"
            onClose={onIntroClose}
          />
        </Suspense>
      )}
      <div
        className={classes.container}
        style={{ opacity: showIntroVideo ? 0 : 1 }}
      >
        <Header />
        <main className={classes.main}>
          <Suspense fallback={null}>
            {isDesktop ? (
              <DesktopLayout
                rightPanelTab={rightPanelTab}
                setRightPanelTab={setRightPanelTab}
                viewCrop={viewCrop}
                setViewCrop={setViewCrop}
                viewSize={viewSize}
                setViewSize={setViewSize}
              />
            ) : (
              <MobileLayout />
            )}
          </Suspense>
        </main>
        {showOverlay && <ResultOverlay onClose={handleCloseResult} />}
        {/* Local notification toast (with action) takes priority over global toast */}
        {notification ? (
          <Toast
            message={notification.message}
            type={notification.type}
            action={notification.action}
            onActionClick={hideNotification}
          />
        ) : (
          globalToast && (
            <Toast message={globalToast.message} type={globalToast.type} />
          )
        )}
      </div>
    </>
  );
};
